import { POI } from '@cya-routes/shared'
import { calculatePOIDistance } from '../../services/poiDistanceService'
import { getPOITypeById, getPOICategoryById } from '../../data/poiTypes'
import { UserPOI } from '../../types/userPois'
import { getUserPOITypeById } from '../../data/userPoiTypes'
import { calculateMultipleUserPOIDistances } from '../../services/userPoiDistanceService'
import { CheckCircle, Clock } from 'lucide-react'

interface POIReferencePanelWithReportsProps {
  selectedRouteId: string | null
  getCurrentRoute: () => any | null
  pois: POI[]
  selectPOI: (poi: POI) => void
  height?: string
  // User POI props
  userPOIs?: UserPOI[]
  showUserPOIs?: boolean
  onUserPOIClick?: (poi: UserPOI) => void
  showResolved?: boolean
  onToggleResolved?: () => void
}

export function POIReferencePanelWithReports({
  selectedRouteId,
  getCurrentRoute,
  pois,
  selectPOI,
  height,
  userPOIs = [],
  showUserPOIs = false,
  onUserPOIClick,
  showResolved = false,
  onToggleResolved
}: POIReferencePanelWithReportsProps) {
  const selectedRoute = getCurrentRoute()
  
  // Filter and sort regular POIs
  const routePOIs = pois.filter(poi => poi.visible !== false)
  const poisWithDistances = selectedRoute ? routePOIs.map(poi => {
    const result = calculatePOIDistance(poi, selectedRoute)
    const poiType = getPOITypeById(poi.type)
    const poiCategory = poiType ? getPOICategoryById(poiType.category) : null
    return {
      ...poi,
      distance: result.distance || 0,
      isOffRoute: result.isOffRoute,
      offRouteDistance: result.offRouteDistance,
      poiType,
      poiCategory,
      isUserPOI: false
    }
  })
  .filter(poi => {
    // Filter out POIs that are more than 20km off route
    if (poi.isOffRoute && poi.offRouteDistance > 20) {
      return false
    }
    return true
  })
  .sort((a, b) => {
    // Sort off-route POIs to the end
    if (a.isOffRoute && !b.isOffRoute) return 1
    if (!a.isOffRoute && b.isOffRoute) return -1
    return a.distance - b.distance
  }) : []

  // Filter and sort user POIs
  const visibleUserPOIs = showResolved ? userPOIs : userPOIs.filter(poi => !poi.resolved)
  const userPoisWithDistances = selectedRoute && showUserPOIs ? 
    calculateMultipleUserPOIDistances(visibleUserPOIs, selectedRoute)
      .filter(item => {
        // Filter out user POIs that are more than 20km off route
        if (item.isOffRoute && item.offRouteDistance > 20) {
          return false
        }
        return true
      })
      .map(item => ({
        ...item.userPoi,
        distance: item.distance,
        isOffRoute: item.isOffRoute,
        offRouteDistance: item.offRouteDistance,
        isUserPOI: true
      })) : []

  // Combine and sort all items
  const allItems = [...poisWithDistances, ...userPoisWithDistances].sort((a, b) => {
    // Sort off-route items to the end
    if (a.isOffRoute && !b.isOffRoute) return 1
    if (!a.isOffRoute && b.isOffRoute) return -1
    return a.distance - b.distance
  })

  const renderUserPOI = (poi: UserPOI & { distance: number; isOffRoute: boolean; offRouteDistance: number }) => {
    const poiType = getUserPOITypeById(poi.type)
    if (!poiType) return null

    const Icon = poiType.icon
    const confirmationCount = Object.keys(poi.confirmations || {}).length
    const outdatedCount = Object.keys(poi.outdatedVotes || {}).length
    const totalVotes = confirmationCount + outdatedCount
    const confirmationRate = totalVotes > 0 ? confirmationCount / totalVotes : 0

    return (
      <div
        key={`user-${poi.id}`}
        className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg hover:bg-gray-700 transition-colors cursor-pointer"
        onClick={() => onUserPOIClick?.(poi)}
      >
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <div 
            className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center"
            style={{ backgroundColor: poi.resolved ? '#6b7280' : poiType.color }}
          >
            <Icon className="w-3 h-3 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-gray-100 truncate">
              {poi.name}
            </div>
            <div className="text-xs text-gray-400 flex items-center gap-2">
              <span>Report by {poi.authorName}</span>
              {totalVotes > 0 && (
                <div className="flex items-center gap-2">
                  {confirmationCount > 0 && (
                    <span className="text-green-500 flex items-center gap-0.5">
                      <CheckCircle className="w-2.5 h-2.5" />
                      {confirmationCount}
                    </span>
                  )}
                  {outdatedCount > 0 && (
                    <span className="text-amber-500 flex items-center gap-0.5">
                      <Clock className="w-2.5 h-2.5" />
                      {outdatedCount}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={`text-xs ml-2 ${poi.isOffRoute ? 'text-orange-400' : 'text-emerald-400 font-mono'}`}>
          {poi.isOffRoute ? `${poi.offRouteDistance.toFixed(1)}km off` : `${poi.distance.toFixed(1)}km`}
        </div>
      </div>
    )
  }

  const renderRegularPOI = (poi: any) => {
    const IconComponent = poi.poiType?.icon
    return (
      <div
        key={poi.id}
        className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg hover:bg-gray-700 transition-colors cursor-pointer"
        onClick={() => selectPOI(poi)}
      >
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          {IconComponent && (
            <div 
              className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center"
              style={{ 
                backgroundColor: poi.poiCategory?.id === 'city-town' 
                  ? '#9ca3af20' 
                  : `${poi.poiCategory?.color || '#6b7280'}20` 
              }}
            >
              <IconComponent 
                className="w-3 h-3" 
                style={{ 
                  color: poi.poiCategory?.id === 'city-town' 
                    ? '#9ca3af' 
                    : (poi.poiCategory?.color || '#6b7280')
                }}
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-gray-100 truncate">
              {poi.name || poi.poiType?.name || 'Unnamed POI'}
            </div>
            <div className="text-xs text-gray-400">
              {poi.poiType?.name}
            </div>
          </div>
        </div>
        <div className={`text-xs ml-2 ${poi.isOffRoute ? 'text-orange-400' : 'text-emerald-400 font-mono'}`}>
          {poi.isOffRoute ? `${poi.offRouteDistance.toFixed(1)}km off` : `${poi.distance.toFixed(1)}km`}
        </div>
      </div>
    )
  }

  return (
    <div className="w-full lg:w-80 flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-gray-300">
          POI Reference
        </label>
        {showUserPOIs && onToggleResolved && (
          <button
            onClick={onToggleResolved}
            className="text-xs text-gray-400 hover:text-gray-300 transition-colors"
          >
            {showResolved ? 'Hide' : 'Show'} resolved
          </button>
        )}
      </div>
      <div 
        className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-4 overflow-y-auto"
        style={height ? { height } : { flex: 1 }}
      >
        {selectedRouteId && selectedRoute ? (
          allItems.length === 0 ? (
            <div className="text-center text-gray-400 py-8">
              <div className="text-xs">No POIs or reports added yet</div>
              <div className="text-xs mt-1">
                {showUserPOIs 
                  ? "Drag POIs from the sidebar or add reports to the map"
                  : "Drag POIs from the sidebar to add them to your route"
                }
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-xs text-gray-400 uppercase tracking-wide mb-4">
                {allItems.length} item{allItems.length !== 1 ? 's' : ''} along route
                {showUserPOIs && userPoisWithDistances.length > 0 && (
                  <span className="ml-2">({userPoisWithDistances.length} report{userPoisWithDistances.length !== 1 ? 's' : ''})</span>
                )}
              </div>
              
              {allItems.map((item) => {
                if (item.isUserPOI) {
                  return renderUserPOI(item as any)
                } else {
                  return renderRegularPOI(item)
                }
              })}
            </div>
          )
        ) : (
          <div className="text-center text-gray-400 py-8">
            <div className="text-sm">No route selected</div>
            <div className="text-xs mt-1">Import a GPX file to see POI references</div>
          </div>
        )}
      </div>
    </div>
  )
}